import type { tFeatureFlags } from './FeatureFlags.types';

/**
 * Single source of the flag defaults (AGI-154 follow-up 2).
 *
 * Moved here from `FeatureFlagsProvider/FeatureFlags.context.ts` in RET-1922
 * so the strict envelope parser can derive its known-key list from this exact
 * literal instead of keeping a second hand-written copy in the service layer
 * (services never import providers — that direction is the layering
 * inversion). The context re-exports this symbol, so every existing
 * `import { DEFAULT_FLAGS } from '.../FeatureFlags.context'` keeps working.
 *
 * Keep every new flag key here only.
 */
export const DEFAULT_FLAGS: tFeatureFlags = {
  'leads_workspace-agent': false,
  'onboarding_auto-set-up': false,
  'leads_smart-lead-bot': false,
  'leads_auto-profiling': false,
  'leads_lead-agent-metrics': false,
  'billing_wapi-wallet': false,
  'leads_home-news-card': false,
  'leads_lead-agent-cap-raise-news': false,
  // RET-1950 (product decision 26-aug) — the only default that is ON. The
  // 25-aug bulk insert gave every live workspace an explicit row, so a MISSING
  // key now means "workspace created after the backfill" and it is born with
  // the Alert Center. An explicit row still wins over this default; the
  // kill-switch is therefore global from here (revert this line), per-workspace
  // only until legacy retires (RET-1951).
  'leads_alert-center': true,
  optimization_center: false,
  'leads_optimization-center': false,
  // AGI-888 (U3): the feed on `/alert-center`, per workspace from the stored row (D0).
  'leads_alert-center-feed': false,
  // U12 — off by default and enabled deliberately: a proration or no-quote
  // defect would otherwise land on every at-limit workspace simultaneously.
  'billing_inline-seat-purchase': false,
  // Off by default: the zone charges money and is released by cohort.
  'billing_past-due-payment-zone': false,
  'leads_analytics-lake-sections': false,
  'leads_analytics-seller-view': false,
  'leads_analytics-seller-filter': false,
  // LEA-85: "Descargar tabla" on the General's cards, internal workspace first.
  'leads_analytics-table-download': false,
  // INT-1500 U12: the "Próximos envíos" bar above the chat composer.
  'chat_upcoming-sends-bar': false,
  // INT-1500 U13: the warning inside the chat's schedule modal.
  'chat_scheduling-risk-warning': false,
  // INT-1500 U14: the chat list's "!" and "No entregados (N)" chip.
  'chat_undelivered-list': false,
  // INT-1500 U15: the campaigns panel explains failures with the chat catalog.
  'campaigns_meta-error-catalog': false,
  'leads_challenge-completion-toast': false,
  // Directory v2 (U7): numbered pages and picks that persist across pages.
  'leads_funnel-bulk-move': false,
  'leads_directory-v2': false,
  'leads_lead-close': false,
  // The split-modal WhatsApp API connection wizard in place of the current modal.
  'integrations_wapi-connection-wizard': false,
  // SRE-3328: the message search (inbox Mensajes and the in-chat search).
  'chat_message-search': false,
  // RET-2425: the low-balance (threshold) auto top-up inside Recargar.
  'billing_threshold-topup': false,
};

/**
 * Every flag key the frontend knows about, DERIVED from `DEFAULT_FLAGS` so the
 * two can never drift (a new flag added to the defaults is recognised by the
 * parser for free). RET-1922: the parser uses this to recognise the FLAT
 * envelope shape — a 200 payload carrying none of these keys is not a flags
 * payload, it is a failure wearing an HTTP 200.
 */
export const FEATURE_FLAG_KEYS = Object.keys(DEFAULT_FLAGS) as Array<
  keyof tFeatureFlags
>;

/**
 * Backoff between flag-resolution attempts (RET-1922). Length + 1 = total
 * attempts (3), total wait 1.3s: the first resolution decides the whole
 * session (the D11 snapshot is first-commit-wins), so a single transient
 * failure must not be what serves the legacy experience for the next hour.
 * This backoff excludes time in the auth interceptor. BUG-1116 holds eligible
 * requests under one boot deadline; when Firebase never settles, flags give
 * up around 28 s rather than 11 s, and /onboarding remains blank meanwhile.
 */
export const FLAG_FETCH_RETRY_DELAYS_MS: readonly number[] = [400, 900];
